import usePricingEditorReducer from 'containers/PriceProductEditor/usePricingEditorState';
import { Facilities } from 'pages/admin/Users/userAdminContext';
import { Facility } from 'store/uiSettings/reducer';
import { PricingGroup, PricingRange, Zone } from 'store/zone/reducer';
import DefaultsJson from './AIDefaults.json';
import jsonFiles from './json-files';
import JsonFile from './json-files';

export enum GeneralStrategy {
  very_aggressive = 0, // EG VERY CONSERVATIVE
  aggressive = 1, // EG CONSERVATIVE
  balanced = 2, // EG BALANCED
  volume_priority = 3, // EG AGGRESSIVE
  high_volume_priority = 4, // EG VERY AGGRESSIVE
}

////////// Legacy, new impl at end of file //////////////////////

const personalityTranslation = (personality: string) => {
  switch (personality) {
    case 'Very Conservative':
      return GeneralStrategy.very_aggressive;
    case 'Conservative':
      return GeneralStrategy.volume_priority;
    case 'Middle':
      return GeneralStrategy.balanced;
    case 'High':
      return GeneralStrategy.aggressive;
    case 'Very High':
      return GeneralStrategy.high_volume_priority;
    default:
      throw Error('Unknown personality: ' + personality);
  }
};

// Classic AI default tables hold this form.
// Has no notion of flexible ranges
export interface AIDefault {
  CliZoneId: number;
  ZoneName: string;
  Kind: 'Driveup' | 'Booking';
  generalStrategy: GeneralStrategy;
  personality: string;
  MinuteMin?: number;
  MinuteMax?: number;
  DayMin?: number;
  DayMax?: number;
  WeekMin?: number;
  WeekMax?: number;
  extraData?: AIExtraData[];
}

// hack to add range support-ish
interface AIExtraData {
  name: string;
  duration: PricingRange['duration'];
  min: number;
  max: number;
  groupId: number;
}

interface JsonData {
  Duration: string;
  Min: number;
  Max: number;
  ExtendedStay?: boolean;
}

const parseNum = (numStr: string) => {
  const num = Number(numStr);
  if (isNaN(num)) {
    return undefined;
  }
  return num;
};

const AIDefaults = DefaultsJson.map(
  (d): AIDefault => ({
    CliZoneId: d.CliZoneId,
    ZoneName: d.ZoneName,
    Kind: d.Kind as any,
    generalStrategy: personalityTranslation(d.Personality),
    personality: d.Personality,
    DayMax: parseNum(d.DayMax),
    DayMin: parseNum(d.DayMin),
    MinuteMax: parseNum(d.MinuteMax),
    MinuteMin: parseNum(d.MinuteMin),
  })
);

export default AIDefaults;

interface FaciilityHandling {
  file: any;
  nameParsing: (zone: Zone, segment: string) => string;
  translateExtra: (
    jsonData: JsonData,
    segment: string,
    priceProductEditorReducer: ReturnType<typeof usePricingEditorReducer>
  ) => AIExtraData[];
}

const FacilityJsonHandling = {
  18: {
    file: JsonFile.OsloAiPricing,
    nameParsing: (zone: Zone, segment: string) => {
      var zoneIdentifer = zone.name.split(' ')[1];
      return `${zoneIdentifer}_${segment}`;
    },
    translateExtra: (
      jsonData: JsonData,
      segment: string,
      priceProductEditorReducer: ReturnType<typeof usePricingEditorReducer>
    ) => {
      // var name = '';
      // switch (jsonData.Duration) {
      //   case 'day10':
      //     name = '10 days+';
      //     break;
      //   default:
      // }
      // if (name === '') {
      //   return [];
      // }
      // var priceGroup = editState.priceGroups.find((f) =>  f.name === name);
      const [editState, actions] = priceProductEditorReducer;

      var priceGroups = editState.priceGroups.filter((f) => {
        if (
          jsonData.ExtendedStay &&
          f.condition &&
          f.condition.durationDayRange &&
          f.condition.durationDayRange[0] > 0
        )
          return true;
        return false;
      });
      var aiExtraData: AIExtraData[] = [];
      if (!priceGroups || !priceGroups.length)
        return aiExtraData; // empty-extra data if we cannot find an matching group
      else {
        var pricings = editState.prices.filter((f) =>
          priceGroups.find((pg) => pg.id === f.groupId && f.segment === segment)
        );
        pricings.forEach((price) => {
          var extra: AIExtraData = {
            duration: price.duration,
            min: Math.round(jsonData.Min),
            max: Math.round(jsonData.Max),
            groupId: price.groupId!,
            name: jsonData.Duration,
          };
          aiExtraData.push(extra);
        });
      }
      return aiExtraData;
    },
  },
} as { [key: number]: FaciilityHandling };

const GeneratedAiDefaultStrategy = {
  [GeneralStrategy.balanced]: 'optimal_pricing',
  [GeneralStrategy.volume_priority]: 'customer_focused_pricing',
  [GeneralStrategy.very_aggressive]: 'revenue_focused_pricing',
};

export const generateAiDefault = (
  facility: Facility,
  zone: Zone,
  generalStrategy: GeneralStrategy,
  priceProductEditorReducer: ReturnType<typeof usePricingEditorReducer>
) => {
  var facilityHandling = FacilityJsonHandling[facility.id];
  if (!facilityHandling.file) {
    return [];
  }
  var strategy =
    GeneratedAiDefaultStrategy[
      generalStrategy as keyof typeof GeneratedAiDefaultStrategy
    ];
  // console.log(PricingRange)
  var aiDefaults: AIDefault[] = facility.segments.reduce(
    (acc, currentSegment) => {
      var name = facilityHandling.nameParsing(zone, currentSegment);
      var jsonData = facilityHandling.file[name][strategy] as JsonData[];
      if (!jsonData) {
        return acc;
      }

      var aiDefault: AIDefault = {
        CliZoneId: zone.id,
        ZoneName: zone.name,
        Kind: (currentSegment[0].toUpperCase() +
          currentSegment.substring(1)) as AIDefault['Kind'],
        generalStrategy: generalStrategy,
        personality: generalStrategy.toString(),
        extraData: [],
      };

      jsonData.forEach((pricing) => {
        if (pricing.ExtendedStay) {
          var extraData = facilityHandling.translateExtra(
            pricing,
            currentSegment,
            priceProductEditorReducer
          );
          aiDefault.extraData = [...aiDefault.extraData!, ...extraData];
          return;
        }
        switch (pricing.Duration) {
          case 'hour':
            aiDefault.MinuteMin = pricing.Min / 60;
            aiDefault.MinuteMax = pricing.Max / 60;
            break;
          case 'day':
            aiDefault.DayMin = Math.round(pricing.Min);
            aiDefault.DayMax = Math.round(pricing.Max);
            break;
          case 'week':
            aiDefault.WeekMin = Math.round(pricing.Min);
            aiDefault.WeekMax = Math.round(pricing.Max);
            break;
          default:
            throw new Error('Unknown duration:' + pricing.Duration);
        }
      });
      return [...acc, aiDefault];
    },
    [] as AIDefault[]
  );
  return aiDefaults;
};

////////////////////////// NEW CODE! //////////////

// Some zones are to be priced like other zones, so we have an alias list to redirect those.

const zoneIdAliases = [
  { z: 31, as: 29 }, // P10 elec as P10
  { z: 32, as: 30 }, // P11 elec as P11
  { z: 34, as: 33 }, // P6 aliased as P4
  { z: 35, as: 37 }, // P1 as P2
  { z: 36, as: 29 }, // P10 PG as P10
  { z: 38, as: 37 }, // P3 as P2
  { z: 39, as: 33 }, // P5 aliased as P4
  { z: 40, as: 33 }, // P7 aliased as P4
] as {
  z: number;
  as: number;
}[];

// This method returns an applicator if there is zone-suggestions for that zone.
export function tryGetAIDefaultApplicator(
  zoneId: string | number,
  suggestedPriceRanges: PricingRange[],
  suggestedPriceRangeGroups: PricingGroup[]
) {
  // First check if we do have an alias? (Maybe zones are compounds so one suggestion covers multiple)
  const aliasInfo = zoneIdAliases.find((t) => t.z === zoneId);
  // If so, use the aliased zoneId
  zoneId = aliasInfo?.as ?? zoneId;

  // Filter out flatly those suggestions that are relevant

  // return an applicator function that will match individual ranges to the pertinent suggestion.
  return (pr: PricingRange, pgs: PricingGroup[]) => {
    console.log('Updating individual price ' + pr);

    // If the range has a group, pick it out first
    const pg = pgs.find((tpg) => tpg.id === pr.groupId);

    var suggestedGroup =
      pg && suggestedPriceRangeGroups.find((spg) => spg.name === pg.name);

    // Now locate the suggestion for this range.
    const suggestion = suggestedPriceRanges.find((se) => {
      return (
        se.segment === pr.segment &&
        se.duration === pr.duration &&
        (!suggestedGroup || se.groupId === suggestedGroup.id)
      );
    });
    // If an suggestion matched, apply it.
    if (suggestion) {
      return {
        ...pr,
        min: Math.round(suggestion.min),
        max: Math.round(suggestion.max),
      };
    }
    // Otherwise retain the original value.
    return pr;
  };
}
